import { useCallback, useRef } from 'react';

import { ClientOnly } from '#/containers/ClientOnly';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import type { ApiEvent } from '#/modules/listing/events/api/types/ApiEvent';
import { trackEvent } from '#/utils/analytics/track/trackEvent';
import { dynamic } from '#/utils/dynamic';

const DynamicOpenWebWidget = dynamic(
  () =>
    import('#/modules/cms/components/Comments/OpenWebWidget').then(
      (module) => ({
        default: module.OpenWebWidget,
      }),
    ),
  { strategy: 'client-only' },
);

type Props = {
  listing: ApiEvent;
};

export function ListingViewCommentSection({ listing }: Props) {
  const { locale } = useI18n();
  const atsRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();

  useElementReachedEffect({
    ref: atsRef,
    onReach: useCallback(() => {
      trackEvent('Section View: Idealist Comments', {
        listing_type: listing.type,
        listing_id: listing.id,
        org_id: listing.org?.id ?? null,
        org_type: listing.org?.orgType ?? null,
      });
    }, [listing.id, listing.type, listing.org?.id, listing.org?.orgType]),
  });

  const listingUrl = listing.url[locale];
  const relativePath = listingUrl.startsWith('/')
    ? listingUrl.slice(1)
    : listingUrl;

  return (
    <ListingViewSection id="comments">
      <ClientOnly>
        <DynamicOpenWebWidget
          postId={listing.id}
          relativePath={relativePath}
          userId={user ? user.id : undefined}
          hasIdealistProfileFeature={
            user ? Boolean(user.hasIdealistProfileFeature) : false
          }
          hasPublicProfile={user ? user.hasPublishedIdealistProfile : false}
        />
      </ClientOnly>
    </ListingViewSection>
  );
}
